<script setup lang="ts">
/**
 * 「熔暮绸缎」交互式 shader 背景 — WebGL1 fragment shader 壁纸。
 *
 * 视觉: 域扭曲 FBM 织出的日落极光绸缎光带 (品牌 5 色锚点渐变, 金色只出现在 ridge 峰值/涟漪环沿)。
 * 交互: 鼠标 = 弹簧阻尼搅拌棒 (快甩留 ~2s 黏性尾迹); 点击 = 折射涟漪 (连点 combo + 干涉);
 *       触摸 = 拖动搅拌 / tap 涟漪 / 长按充能松手爆发; idle 金色流星; Konami storm 全面爆发。
 * 性能: 噪声 LUT 纹理 + 半分辨率渲染 + CSS 上采样; window load 后 requestIdleCallback 才初始化
 *       (Lighthouse 测量窗口零成本); KHR_parallel_shader_compile 异步编译不阻塞交互;
 *       软渲染器(SwiftShader/CI headless) 与无 highp fragment 的老设备直接回退 CSS 版;
 *       idle 降 30fps, tab 隐藏全停; 自适应降档按实测原生 rAF tick 判定 (75Hz/省电节流不误伤)。
 * 降级: GL 失败 / 软渲染 / 无 highp / context lost 不恢复(4s watchdog) / 降档到底
 *       → 回退 CSS AuroraBg (storm prop 直通)。prefers-reduced-motion → 渲一帧静态画面停帧,
 *       关闭交互 (resize/restore 后会补绘, 不会黑屏)。
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AuroraBg from '@/components/AuroraBg.vue';
import { useTextSelect } from '@/composables/useTextSelect';
import { SHADER_THEMES, type ShaderThemeDef, type ShaderThemeName } from '@/config/shader-themes';

export type { ShaderThemeName };

const props = defineProps<{ storm?: boolean; theme?: ShaderThemeName }>();

// 桌面长按充能与「拖选文字」互斥: 拖选关 (默认) → 充能开; 拖选开 → 充能关
// (按住拖动既要选字又要充能会打架, 二选一, 跟随用户在 /settings 的选择)
const { textSelect } = useTextSelect();

const canvasEl = ref<HTMLCanvasElement | null>(null);
const glAlive = ref(false); // canvas 是否挂载 (v-if)
const glShown = ref(false); // crossfade: 首帧成功后置 true → opacity 0→1
const cssGone = ref(false); // fade 完成后卸载 CSS 版

// ── GLSL ──────────────────────────────────────────────────────────────────

const VERT = `attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }`;

// OCTAVES / TRAIL_N / USE_WARP2 由 JS 按档位注入 #define, 编译唯一变体
// (移动 GPU 上 uniform 分支不可靠省功耗; TRAIL_N=0 时尾迹循环被编译器整段消除)。
// 初始化时已强制要求 highp fragment (无则回退 CSS), 所以这里 highp 一定生效 —
// mediump fp16 下大 uTime 的 ulp 会让动画抽帧/冻结, 不进这条路径。
const FRAG_BODY = `
precision highp float;

uniform vec2 uResolution;
uniform float uTime;       // JS 侧积分的虚拟时间 (storm/idle 变速无相位跳变)
uniform vec2 uMouse;       // 场坐标 (aspect 校正, y 上)
uniform vec2 uMouseVel;    // 弹簧平滑速度 (方向=推开方向, 模长=搅动强度)
uniform float uStir;       // 累积搅动能量 0..1
uniform vec4 uTrail[4];    // 尾迹槽 (xy=位置, zw=沉积速度×衰减)
uniform vec4 uRipples[4];  // 涟漪槽 (xy=中心, z=出生虚拟时刻, w=幅度)
uniform float uPress;      // 触摸长按充能 0..1
uniform float uStorm;      // Konami 风暴 0..1 (JS 缓动)
uniform float uIdle;       // 静置度 0..1
uniform float uMaskHalf;   // 中央内容列半宽 (场单位)
uniform sampler2D uNoiseTex;
// 主题参数 (uniform 驱动 → 切主题无需重编译, JS 侧 lerp 实现平滑过渡):
uniform vec3 uPalA;        // 5 锚点 ramp, JS 已预平方 (伪线性域)
uniform vec3 uPalB;
uniform vec3 uPalC;
uniform vec3 uPalD;
uniform vec3 uPalE;
uniform vec3 uBaseCol;     // 底部渐隐目标色 (输出 sRGB 域)
uniform vec4 uTune1;       // x=fieldLo y=fieldHi z=exposure w=ridgeGain
uniform vec2 uTune2;       // x=maskDim (中央列压暗) y=ambientSparkle (子夜常驻星屑)

uniform vec3 uGlow;        // 高光/涟漪光晕/长按充能/流星/金屑点缀色 (主题驱动, 替代旧硬编码暖金)
uniform vec3 uRimA;        // 点击涟漪环沿虹彩外锚 (暖色玫瑰 / polar 品红)
uniform vec3 uRimB;        // 点击涟漪环沿虹彩内锚 (暖色珊瑚 / polar 青)

// LUT value noise: 一次 fetch + 坐标 smoothstep 校正 = C1 连续, 无 fract(sin) 精度坑
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return texture2D(uNoiseTex, (i + f + 0.5) * (1.0 / 256.0)).r;
}

const mat2 ROT2 = mat2(0.8, 0.6, -0.6, 0.8); // 八度间旋转杀轴对齐

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < OCTAVES; i++) {
    v += a * noise(p);
    p = ROT2 * p * 2.02 + vec2(13.1, 7.7);
    a *= 0.5;
  }
  return v;
}

float ridge(vec2 p) { return 1.0 - abs(2.0 * noise(p) - 1.0); }

// 鼠标搅动透镜 + 尾迹核: 返回采样域偏移, out 局部"热度" (搅动摩擦生热提亮)
vec2 stirOffset(vec2 q, out float heat) {
  vec2 d = q - uMouse;
  float fall = exp(-dot(d, d) * 21.0);
  vec2 off = uMouseVel * fall * 0.055;
  heat = fall * uStir;
  for (int i = 0; i < TRAIL_N; i++) {
    vec2 td = q - uTrail[i].xy;
    float tf = exp(-dot(td, td) * 30.0);
    off += uTrail[i].zw * tf * 0.3;
    heat += tf * length(uTrail[i].zw) * 1.6;
  }
  return off;
}

// 解析涟漪: 波前减速扩张 (r=v·sqrt(age)) + exp 衰减 + 1/sqrt(r) 能量摊薄。
// 既位移采样域 (折射, 光带真的被弯折) 又输出环沿强度/相位 (薄膜色)。多波线性叠加 = 干涉。
vec2 rippleField(vec2 q, out float rim, out float rimPhase) {
  vec2 disp = vec2(0.0);
  rim = 0.0;
  rimPhase = 0.0;
  for (int i = 0; i < 4; i++) {
    vec4 R = uRipples[i];
    float age = uTime - R.z;
    if (R.w > 0.001 && age > 0.0 && age < 4.0) {
      float r = 0.55 * sqrt(age + 0.015);
      vec2 d = q - R.xy;
      float dist = length(d);
      float x = (dist - r) * 18.0;
      float band = exp(-x * x);
      float amp = R.w * exp(-age * 1.0) * inversesqrt(r + 0.7);
      float a = band * amp;
      disp += (d / max(dist, 0.001)) * a * 0.13;
      rim += a;
      rimPhase += a * (dist - r) * 6.0;
    }
  }
  return disp;
}

// 主题 5 锚点渐变 (uniform, JS 已预平方 — 伪线性域 mix 防暖色过渡发灰), 出口 sqrt 回 sRGB
vec3 pal(float t) {
  t = clamp(t, 0.0, 1.0);
  vec3 c = mix(uPalA, uPalB, smoothstep(0.0, 0.38, t));
  c = mix(c, uPalC, smoothstep(0.38, 0.62, t));
  c = mix(c, uPalD, smoothstep(0.62, 0.82, t));
  c = mix(c, uPalE, smoothstep(0.82, 1.0, t));
  return sqrt(c);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution.xy;
  float aspect = uResolution.x / uResolution.y;
  vec2 q = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  // 交互场: 折射在 warp 之前 — 涟漪/搅动弯折的是绸缎本身
  float heat;
  vec2 stir = stirOffset(q, heat);
  float rim;
  float rimPhase;
  vec2 rip = rippleField(q, rim, rimPhase);
  vec2 p = q + stir + rip;

  float t = uTime;
  vec2 flow = vec2(0.04, 0.026) * t;

  // 旋转 + 各向异性拉伸 → 对角向绸缎光带 (aurora 帘幕的经典塑形)
  vec2 P = mat2(0.9394, -0.3429, 0.3429, 0.9394) * p;
  P = vec2(P.x * 0.85, P.y * 2.0) + vec2(0.0, 0.5);

  float w1 = fbm(P + flow);
  float w2 = fbm(P * 1.27 - flow * 0.7 + vec2(3.7, 8.1));
  vec2 warp = vec2(w1, w2) - 0.47;
#ifdef USE_WARP2
  warp += 0.65 * (vec2(fbm(P + warp * 1.4 + flow * 0.5),
                       fbm(P - warp.yx * 1.2 + vec2(7.31, 2.17))) - 0.47);
#endif
  float wAmp = 1.25 * (1.0 + uStorm); // storm warp 幅度 ×2 光带翻卷
  float field = fbm(P + warp * wAmp);

  // 基线亮度按主题调 (fieldLo/Hi): 熔暮偏暗、破晓更多区域走亮端、子夜大部分纯黑;
  // storm 时整体推向暖端
  float tt = smoothstep(uTune1.x, uTune1.y, field) + uStorm * 0.22;
  tt *= 1.0 + 0.05 * sin(t * 0.3) * uIdle; // idle 呼吸
  vec3 col = pal(tt);

  // ridged 金丝: 只有峰值前 ~5% 烫到金 (金色稀缺=贵气)。
  // 频率要够高 — 低频 ridge 的闭合等高线会形成大圆环, 看起来像卡住的假涟漪
  float rg = ridge(P * 3.4 + warp * wAmp * 1.6 + flow * 0.5);
  col += uGlow * pow(rg, 9.0) * (0.12 + 0.5 * tt) * (uTune1.w + 0.9 * uStorm);

  // 涟漪环沿薄膜虹彩: 洋红→珊瑚→金 随波相位滚动 (亮色直加, 避免低强度金叠紫底发污)
  float ph = clamp(0.5 + rimPhase * 1.2, 0.0, 1.0);
  vec3 rimCol = mix(uRimA, mix(uRimB, uGlow, ph), ph);
  col += rimCol * min(rim * 1.25, 0.85);

  // 搅动生热微提亮
  col *= 1.0 + 0.32 * min(heat, 1.2);

  // 触摸长按充能金晕 (屏息感) + 随充能扩张的细环 — 手指遮住中心也能看到反馈
  vec2 dm = q - uMouse;
  float halo = uPress * exp(-dot(dm, dm) * mix(40.0, 8.0, uPress));
  col += uGlow * halo * 0.8;
  if (uPress > 0.01) {
    float pr = 0.08 + uPress * 0.3;
    float pd = length(dm) - pr;
    col += uGlow * exp(-pd * pd * 900.0) * uPress * 0.6;
  }

  // idle 金色流星: 每 ~34s 一条, hash 伪随机轨迹, 只在静置时可见 (奖励停留者)
  float mcell = floor(t * (1.0 / 34.0));
  float msec = t - mcell * 34.0;
  if (msec < 1.6 && uIdle > 0.35) {
    float mr = noise(vec2(mcell * 1.37, 9.13) * 17.0);
    float prog = msec / 1.6;
    vec2 mdir = normalize(vec2(0.94, -0.34));
    vec2 mp = vec2(-aspect * 0.65, (mr - 0.5) * 0.7 + 0.15) + mdir * prog * (aspect * 1.5);
    vec2 dmm = q - mp;
    float along = dot(dmm, mdir);
    float ortho = dot(dmm, vec2(-mdir.y, mdir.x));
    float head = exp(-dot(dmm, dmm) * 900.0);
    // exp 参数先 clamp 防溢出 (Inf×0=NaN); step 门控保留区间裁剪
    float tail = exp(-ortho * ortho * 2200.0) * exp(clamp(along, -0.35, 0.0) * 10.0)
      * step(abs(along + 0.175), 0.175);
    col += uGlow * (head * 1.1 + tail * 0.45) * sin(prog * 3.14159) * uIdle;
  }

  // 金屑 sparkle: storm 门控 + 子夜主题常驻星屑 (uTune2.y), 其他主题平时零成本
  if (uStorm + uTune2.y > 0.005) {
    float sp = pow(noise(q * 23.0 + vec2(t * 8.1, -t * 5.7)), 16.0);
    col += uGlow * sp * (uStorm * 1.6 + uTune2.y);
  }

  // 中央内容列压暗 (阅读优先, 主题可调狠度, storm 时放开) + 底部渐隐入主题底色
  float cd = mix(1.0, uTune2.x, smoothstep(uMaskHalf + 0.34, uMaskHalf - 0.12, abs(q.x)));
  cd = mix(cd, 1.0, uStorm * 0.55);
  col *= cd;
  col = mix(col, uBaseCol, smoothstep(0.22, 0.0, uv.y) * 0.6);

  // storm 饱和度推升
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(vec3(lum), col, 1.0 + 0.35 * uStorm);

  // 高光软压缩 — 必须在全部叠加之后, 否则珊瑚+金干涉处爆白
  lum = dot(col, vec3(0.299, 0.587, 0.114));
  col /= 1.0 + max(lum - 0.62, 0.0) * 1.4;
  col *= uTune1.z;

  // IGN 抖动杀深紫 banding (OLED 上必现, 不可省)
  float ign = fract(52.9829189 * fract(0.06711056 * gl_FragCoord.x + 0.00583715 * gl_FragCoord.y));
  col += vec3((ign - 0.5) * (1.5 / 255.0));

  gl_FragColor = vec4(col, 1.0);
}`;

// ── 主题 ──────────────────────────────────────────────────────────────────
// 调色板定义抽到 @/config/shader-themes (配置文件渠道, 设计师改那里)。这里只消费 + 编参数。
type ThemeDef = ShaderThemeDef;
const THEMES = SHADER_THEMES;

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

// 33 floats: [0-14] 5 锚点已平方 rgb / [15-17] baseCol sRGB / [18-23] 调参 / [24-26] glow / [27-29] rimA / [30-32] rimB
function themeParams(t: ThemeDef): Float32Array {
  const a = new Float32Array(33);
  t.anchors.forEach((hex, i) => {
    const [r, g, b] = hexToRgb(hex);
    a[i * 3] = r * r;
    a[i * 3 + 1] = g * g;
    a[i * 3 + 2] = b * b;
  });
  const [br, bg, bb] = hexToRgb(t.base);
  a[15] = br;
  a[16] = bg;
  a[17] = bb;
  a[18] = t.fieldLo;
  a[19] = t.fieldHi;
  a[20] = t.exposure;
  a[21] = t.ridgeGain;
  a[22] = t.maskDim;
  a[23] = t.sparkle;
  // glow / rimA / rimB: 点缀色, 直接 sRGB 域 (不预平方, 同 baseCol), 主题间随 cur→target lerp 平滑过渡
  const [glr, glg, glb] = hexToRgb(t.glow);
  a[24] = glr;
  a[25] = glg;
  a[26] = glb;
  const [rar, rag, rab] = hexToRgb(t.rimA);
  a[27] = rar;
  a[28] = rag;
  a[29] = rab;
  const [rbr, rbg, rbb] = hexToRgb(t.rimB);
  a[30] = rbr;
  a[31] = rbg;
  a[32] = rbb;
  return a;
}

// ── 状态 ──────────────────────────────────────────────────────────────────

let gl: WebGLRenderingContext | null = null;
let program: WebGLProgram | null = null;
let vbo: WebGLBuffer | null = null;
let noiseTex: WebGLTexture | null = null;
let loc: Record<string, WebGLUniformLocation | null> = {};
let rafId = 0;
let destroyed = false;
let staticMode = false; // prefers-reduced-motion: 渲一帧即停
let restoreCount = 0;

// 档位
let resMul = 0.6;
let cap30 = false;
let trailN = 4;
let octaves = 4;
let useWarp2 = true;

// 主题参数向量 (cur 每帧向 target lerp ≈600ms, 切主题平滑过渡)
const themeCur: Float32Array = themeParams(THEMES[props.theme ?? 'dusk']);
let themeTarget: Float32Array = themeCur.slice();

// 时间 / 输入
let shaderTime = 0;
let lastDraw = 0;
let lastInput = 0;
let idleF = 0;
let stormF = 0;
let stormAcc = 0;
let prevStorm = false;

// 弹簧指针 (临界阻尼, ~300ms 收敛 → 光斑像有质量的实物)
const SPRING_W = 9;
let px = 0;
let py = 0.05;
let tx = 0;
let ty = 0.05;
let vx = 0;
let vy = 0;
let stirE = 0;

// 尾迹 / 涟漪环形缓冲
const trail = new Float32Array(16);
let trailView: Float32Array = trail;
let trailIdx = 0;
let lastDeposit = 0;
const ripples = new Float32Array(16);
let rippleIdx = 0;
let lastClick = 0;
let comboMul = 1;

// 触摸手势
let touchId: number | null = null;
let touchStartX = 0;
let touchStartY = 0;
let touchStartT = 0;
let touchScroll = false;
let touchDrag = false;
let pressF = 0;

// 鼠标长按充能 (文字选择默认关闭后, 桌面长按与拖选的冲突已不存在):
// 按下出即时涟漪 (原行为) + 原地按住 ≥300ms 开始充能, 移动 >12px 视为拖动取消
let mouseDown = false;
let mouseDownX = 0;
let mouseDownY = 0;
let mouseDownT = 0;

// 自适应降档 (单向)。慢帧判定基于实测原生 rAF tick (tickEma),
// 不写死 60Hz — 75Hz 屏 / 省电模式 rAF 节流 / 30Hz 外接屏都不会被误判为 GPU 慢。
let slowFrames = 0;
let frameCount = 0;
let downStep = 0;
let tickEma = 16.7;
let lastRaf = 0;

let aspect = 1;
let maskHalf = 0.5;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
let fadeTimer: ReturnType<typeof setTimeout> | null = null;
let lostTimer: ReturnType<typeof setTimeout> | null = null;
let reduceMq: MediaQueryList | null = null;
let onReduceChange: (() => void) | null = null;
let loadHandler: (() => void) | null = null;

// ── 工具 ──────────────────────────────────────────────────────────────────

// 用 documentElement.clientWidth/Height 而非 innerWidth/Height:
// fixed 元素的包含块 (ICB) 不含经典滚动条, Windows/Linux 常驻滚动条下
// innerWidth 比画布实际显示宽 ~15px, 会让靠右的点击/搅动坐标偏离光标
function viewSize(): [number, number] {
  const d = document.documentElement;
  return [d.clientWidth || window.innerWidth || 1, d.clientHeight || window.innerHeight || 1];
}

function toField(clientX: number, clientY: number): [number, number] {
  const [w, h] = viewSize();
  return [(clientX / w - 0.5) * (w / h), 0.5 - clientY / h];
}

function markInput(now: number) {
  lastInput = now;
}

function injectRipple(x: number, y: number, amp: number) {
  const i = rippleIdx % 4;
  ripples[i * 4] = x;
  ripples[i * 4 + 1] = y;
  ripples[i * 4 + 2] = shaderTime;
  ripples[i * 4 + 3] = amp;
  rippleIdx++;
}

function comboAmp(base: number, now: number): number {
  comboMul = now - lastClick < 600 ? Math.min(comboMul * 1.2, 2) : 1;
  lastClick = now;
  return base * comboMul;
}

function clearTimers() {
  if (fadeTimer) {
    clearTimeout(fadeTimer);
    fadeTimer = null;
  }
  if (lostTimer) {
    clearTimeout(lostTimer);
    lostTimer = null;
  }
  if (resizeTimer) {
    clearTimeout(resizeTimer);
    resizeTimer = null;
  }
}

function detachListeners() {
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerdown', onPointerDown);
  window.removeEventListener('pointerup', onPointerUp);
  window.removeEventListener('pointercancel', onPointerCancel);
  window.removeEventListener('contextmenu', onContextMenu);
  window.removeEventListener('blur', onBlur);
  window.removeEventListener('resize', onResize);
  document.removeEventListener('visibilitychange', onVisibility);
  if (reduceMq && onReduceChange) reduceMq.removeEventListener?.('change', onReduceChange);
}

// 回退 CSS 版。必须清 fadeTimer — 否则 crossfade 700ms 窗口内降级时,
// 旧 timer 到点会把 cssGone 翻回 true, GL 和 CSS 两个背景全被卸载, 页面只剩裸底色。
function toCss() {
  stopLoop();
  clearTimers();
  detachListeners();
  releaseGl();
  glAlive.value = false;
  glShown.value = false;
  cssGone.value = false; // CSS AuroraBg 顶上 (storm prop 直通)
}

// ── 事件 ──────────────────────────────────────────────────────────────────

function onPointerMove(e: PointerEvent) {
  if (staticMode) return;
  const now = performance.now();
  if (e.pointerType === 'mouse') {
    [tx, ty] = toField(e.clientX, e.clientY);
    markInput(now);
    // 按住后移动超过 12px = 拖动/选择手势, 取消充能
    if (mouseDown && Math.hypot(e.clientX - mouseDownX, e.clientY - mouseDownY) > 12) {
      mouseDown = false;
    }
    return;
  }
  if (e.pointerId !== touchId) return;
  const dx = e.clientX - touchStartX;
  const dy = e.clientY - touchStartY;
  // 滚动意图判定: 垂直主导 = 页面滚动, 整段手势不再注入搅动。
  // 充能已开始 (pressF≥0.15) 后给手指漂移豁免 — 长按时的微小位移不打断充能
  if (!touchScroll && !touchDrag && pressF < 0.15 && (Math.abs(dx) > 18 || Math.abs(dy) > 18)) {
    if (Math.abs(dy) > Math.abs(dx)) touchScroll = true;
    else touchDrag = true;
  }
  if (!touchScroll) {
    [tx, ty] = toField(e.clientX, e.clientY);
    markInput(now);
  }
}

function onPointerDown(e: PointerEvent) {
  if (staticMode) return;
  const now = performance.now();
  markInput(now);
  if (e.pointerType === 'mouse') {
    if (e.button !== 0) return; // 只响应主键 (右键留给系统菜单)
    const [fx, fy] = toField(e.clientX, e.clientY);
    injectRipple(fx, fy, comboAmp(0.9, now)); // 按下即时涟漪 (点击手感), 充能是按住后的追加奖励
    mouseDown = textSelect.value === 'off'; // 拖选文字开启时不充能 (手势让给选字)
    mouseDownX = e.clientX;
    mouseDownY = e.clientY;
    mouseDownT = now;
    return;
  }
  // 触摸: 灯笼瞬移到指尖, 记录手势起点 (tap/长按/滚动在 up 时裁决)
  touchId = e.pointerId;
  touchStartX = e.clientX;
  touchStartY = e.clientY;
  touchStartT = now;
  touchScroll = false;
  touchDrag = false;
  pressF = 0;
  const [fx, fy] = toField(e.clientX, e.clientY);
  px = tx = fx;
  py = ty = fy;
  vx = vy = 0;
}

function onPointerUp(e: PointerEvent) {
  if (staticMode) return;
  const now = performance.now();
  // 鼠标: 充能过 (≥0.15) 才追加爆发涟漪, 普通点击的涟漪在 pointerdown 已经出过了
  if (e.pointerType === 'mouse') {
    if (mouseDown && pressF > 0.15) {
      const [fx, fy] = toField(e.clientX, e.clientY);
      injectRipple(fx, fy, 1.0 + pressF * 1.4);
    }
    mouseDown = false;
    markInput(now);
    return;
  }
  if (e.pointerId !== touchId) return;
  if (!touchScroll && !touchDrag) {
    const held = now - touchStartT;
    const [fx, fy] = toField(e.clientX, e.clientY);
    // 长按充能 → 松手按充能量炸更大涟漪; 快 tap = ×1.3 幅度 (无 hover 预热, 首触反馈更强)
    const base = held >= 300 ? 1.0 + pressF * 1.4 : 1.15;
    injectRipple(fx, fy, comboAmp(base, now));
  }
  touchId = null;
  markInput(now);
}

// pointercancel = 手势被系统接管 (浏览器开始滚动/来电打断), 永远不是 tap。
// 浏览器滚动接管阈值 (~8-10px) 低于我们的判定, 若复用 onPointerUp
// 会让移动端每次下滑起手都炸涟漪、还把 idle 省电档的 lastInput 不停重置。
function onPointerCancel(e: PointerEvent) {
  if (e.pointerId === touchId) {
    touchId = null;
    pressF = 0;
  }
  if (e.pointerType === 'mouse') mouseDown = false;
}

// 窗口失焦兜底: 在窗外松开鼠标收不到 pointerup, 不清会卡在充能态
function onBlur() {
  mouseDown = false;
  touchId = null;
  pressF = 0;
}

// Android Chrome 长按 ~500ms 会触发 contextmenu (随后 pointercancel), 把充能手势掐死。
// 仅在触摸手势活跃时阻止 — 桌面鼠标右键 (touchId 为 null) 完全不受影响。
function onContextMenu(e: Event) {
  if (touchId !== null) e.preventDefault();
}

function onVisibility() {
  if (document.hidden) {
    stopLoop();
  } else if (gl && !destroyed && !staticMode) {
    lastDraw = performance.now();
    lastRaf = lastDraw;
    startLoop();
  }
}

function onResize() {
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(applySize, 200);
}

function applySize() {
  const c = canvasEl.value;
  if (!c || !gl) return;
  const [cssW, cssH] = viewSize();
  const w = Math.max(8, Math.round(cssW * resMul));
  const h = Math.max(8, Math.round(cssH * resMul));
  if (c.width !== w || c.height !== h) {
    c.width = w;
    c.height = h;
  }
  gl.viewport(0, 0, w, h);
  aspect = cssW / cssH;
  maskHalf = (Math.min(1100, cssW * 0.92) * 0.5) / cssH;
  // 设置 canvas.width 会把 drawing buffer 清成黑 — 立即补绘一帧。
  // staticMode (reduced-motion 停帧) 下这是唯一的重绘机会, 不补 = 旋屏后永久黑屏
  if (program) draw();
}

// ── 渲染循环 ──────────────────────────────────────────────────────────────

function startLoop() {
  if (!rafId && !destroyed) rafId = requestAnimationFrame(frame);
}

function stopLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
}

function frame(nowMs: number) {
  rafId = 0;
  if (destroyed || !gl || !program) return;
  startLoop();

  // 实测原生 rAF tick (EMA): 慢帧判定的基准, 兼容 75/120Hz 屏与省电节流
  if (lastRaf > 0) {
    const rawDt = nowMs - lastRaf;
    if (rawDt > 2 && rawDt < 120) tickEma += (rawDt - tickEma) * 0.05;
  }
  lastRaf = nowMs;

  const interval = cap30 || idleF > 0.6 ? 33 : 16;
  if (nowMs - lastDraw < interval - 1) return;
  const dtMs = nowMs - lastDraw;
  const dt = Math.min(dtMs / 1000, 0.05);
  lastDraw = nowMs;

  // 自适应降档 (单向): 健康绘制间隔 = 按原生 tick 凑齐目标 interval 的整拍;
  // 显著超过 (多吃 0.6 拍以上) 才算慢帧。cap30 后继续统计 — 30fps 仍跑不动的
  // 设备最终走到第三级 toCss (否则 CSS 兜底是死代码, 最弱设备反而永远卡在 GL)
  const drawTarget = Math.ceil((interval - 1) / tickEma) * tickEma;
  frameCount++;
  if (dtMs > drawTarget + tickEma * 0.6) slowFrames++;
  if (frameCount >= 120) {
    if (slowFrames / frameCount > 0.4) {
      downStep++;
      if (downStep === 1) {
        resMul *= 0.75;
        applySize();
      } else if (downStep === 2) {
        cap30 = true;
      } else {
        toCss();
        return;
      }
    }
    frameCount = 0;
    slowFrames = 0;
  }

  // storm / idle 缓动 (绝不直通 boolean — 0.4s in / 1.2s out)
  const stormTarget = props.storm ? 1 : 0;
  stormF += (stormTarget - stormF) * Math.min(1, (dt / (stormTarget > stormF ? 0.4 : 1.2)) * 3);
  stormF = Math.max(0, Math.min(1, stormF));
  const wantIdle = nowMs - lastInput > 8000 ? 1 : 0;
  idleF += (wantIdle - idleF) * Math.min(1, (dt / (wantIdle ? 2.5 : 0.3)) * 3);

  // 虚拟时间积分: storm ×4 / idle ×0.6 全在系数上, 零相位跳变。
  // highp fp32 下 65536 处 ulp 仍 <8ms, 回卷需可见态连跑 ~18h+, 实际不可达;
  // 真到了也只平移涟漪出生时刻 (其余时间项单帧跳变一次, 可接受)
  const speedFactor = (1 + 3 * stormF) * (1 - 0.4 * idleF * (1 - stormF));
  shaderTime += dt * speedFactor;
  if (shaderTime > 65536) {
    shaderTime -= 65536;
    for (let i = 0; i < 4; i++) ripples[i * 4 + 2] -= 65536;
  }

  // storm 自动涟漪: 每 ~300ms 随机位置满幅波, 满屏干涉
  if (stormF > 0.55) {
    stormAcc += dt;
    if (stormAcc > 0.3) {
      stormAcc = 0;
      injectRipple((Math.random() - 0.5) * aspect * 0.8, (Math.random() - 0.5) * 0.8, 1.8);
    }
  }

  // 弹簧积分 (临界阻尼)
  vx += (SPRING_W * SPRING_W * (tx - px) - 2 * SPRING_W * vx) * dt;
  vy += (SPRING_W * SPRING_W * (ty - py) - 2 * SPRING_W * vy) * dt;
  px += vx * dt;
  py += vy * dt;
  let sp = Math.hypot(vx, vy);
  if (sp > 2.6) {
    vx *= 2.6 / sp;
    vy *= 2.6 / sp;
    sp = 2.6;
  }

  // 搅动能量 + 尾迹沉积 (速度超阈值每 ≥60ms 落一个核, JS 侧指数衰减 ~2s 黏性松弛)
  stirE = Math.min(1, stirE + sp * dt * 0.55) * Math.exp(-dt / 0.9);
  if (sp > 0.6 && nowMs - lastDeposit > 60 && trailN > 0) {
    const i = trailIdx % trailN;
    trail[i * 4] = px;
    trail[i * 4 + 1] = py;
    trail[i * 4 + 2] = vx * 0.16;
    trail[i * 4 + 3] = vy * 0.16;
    trailIdx++;
    lastDeposit = nowMs;
  }
  const decay = Math.exp(-dt / 0.7);
  for (let i = 0; i < 4; i++) {
    trail[i * 4 + 2] *= decay;
    trail[i * 4 + 3] *= decay;
  }

  // 长按充能 (触摸 / 鼠标共用): 300ms 起步, ~0.9s 充满 — 反馈要快, 否则用户以为没这功能
  const pressT =
    touchId !== null && !touchScroll && !touchDrag ? touchStartT : mouseDown ? mouseDownT : 0;
  if (pressT > 0) {
    const held = nowMs - pressT;
    pressF = held > 300 ? Math.min(1, (held - 300) / 900) : 0;
  } else {
    pressF *= Math.exp(-dt / 0.15);
  }

  // 主题参数向 target lerp (≈600ms), 切主题时整套配色/调参平滑渐变
  const tk = Math.min(1, dt * 3);
  for (let i = 0; i < 33; i++) themeCur[i] += (themeTarget[i] - themeCur[i]) * tk;

  draw();
}

function draw() {
  if (!gl || !program) return;
  gl.useProgram(program);
  gl.uniform2f(loc.uResolution, gl.drawingBufferWidth, gl.drawingBufferHeight);
  gl.uniform1f(loc.uTime, shaderTime);
  gl.uniform2f(loc.uMouse, px, py);
  gl.uniform2f(loc.uMouseVel, vx, vy);
  gl.uniform1f(loc.uStir, stirE);
  // TRAIL_N < 4 时 shader 的活跃数组可能被截短, 只上传活跃长度防 INVALID_OPERATION
  if (trailN > 0 && loc.uTrail) gl.uniform4fv(loc.uTrail, trailView);
  gl.uniform4fv(loc.uRipples, ripples);
  gl.uniform1f(loc.uPress, pressF);
  gl.uniform1f(loc.uStorm, stormF);
  gl.uniform1f(loc.uIdle, idleF);
  gl.uniform1f(loc.uMaskHalf, maskHalf);
  const c = themeCur;
  gl.uniform3f(loc.uPalA, c[0], c[1], c[2]);
  gl.uniform3f(loc.uPalB, c[3], c[4], c[5]);
  gl.uniform3f(loc.uPalC, c[6], c[7], c[8]);
  gl.uniform3f(loc.uPalD, c[9], c[10], c[11]);
  gl.uniform3f(loc.uPalE, c[12], c[13], c[14]);
  gl.uniform3f(loc.uBaseCol, c[15], c[16], c[17]);
  gl.uniform4f(loc.uTune1, c[18], c[19], c[20], c[21]);
  gl.uniform2f(loc.uTune2, c[22], c[23]);
  gl.uniform3f(loc.uGlow, c[24], c[25], c[26]);
  gl.uniform3f(loc.uRimA, c[27], c[28], c[29]);
  gl.uniform3f(loc.uRimB, c[30], c[31], c[32]);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
}

// ── 初始化 ────────────────────────────────────────────────────────────────

function compileShader(g: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = g.createShader(type);
  if (!s) return null;
  g.shaderSource(s, src);
  g.compileShader(s);
  return s;
}

// 编译 + link, 不查状态 (留给 linkAsync 异步轮询, 避免同步等编译阻塞主线程)
function buildProgram(g: WebGLRenderingContext): WebGLProgram | null {
  const defs = `#define OCTAVES ${octaves}\n#define TRAIL_N ${trailN}\n${useWarp2 ? '#define USE_WARP2\n' : ''}`;
  const vs = compileShader(g, g.VERTEX_SHADER, VERT);
  const fs = compileShader(g, g.FRAGMENT_SHADER, defs + FRAG_BODY);
  if (!vs || !fs) return null;
  const prog = g.createProgram();
  if (!prog) return null;
  g.attachShader(prog, vs);
  g.attachShader(prog, fs);
  g.linkProgram(prog);
  return prog;
}

// KHR_parallel_shader_compile: 轮询完成状态再查 LINK_STATUS。
// 同步 getProgramParameter(LINK_STATUS) 会强制等编译 (Windows ANGLE 可达 50-300ms),
// 而 init 落在 load 后 0-2s — 正是用户首次滚动/点击的窗口, 不能吃这个长任务。
function linkAsync(g: WebGLRenderingContext, prog: WebGLProgram, cb: (ok: boolean) => void) {
  const ext = g.getExtension('KHR_parallel_shader_compile') as {
    COMPLETION_STATUS_KHR: number;
  } | null;
  if (!ext) {
    cb(!!g.getProgramParameter(prog, g.LINK_STATUS));
    return;
  }
  const t0 = performance.now();
  const poll = () => {
    if (destroyed || !gl) return;
    if (
      g.getProgramParameter(prog, ext.COMPLETION_STATUS_KHR) === true ||
      performance.now() - t0 > 5000
    ) {
      cb(!!g.getProgramParameter(prog, g.LINK_STATUS));
    } else {
      setTimeout(poll, 24);
    }
  };
  setTimeout(poll, 0);
}

// program 就绪后的剩余 GL 资源: 顶点 buffer + 噪声 LUT + uniform locations
function finishGlObjects(g: WebGLRenderingContext, prog: WebGLProgram): boolean {
  // fullscreen triangle (WebGL1 无 gl_VertexID, 必须真实 attribute buffer)
  vbo = g.createBuffer();
  g.bindBuffer(g.ARRAY_BUFFER, vbo);
  g.bufferData(g.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), g.STATIC_DRAW);
  const aPos = g.getAttribLocation(prog, 'aPos');
  if (aPos < 0) return false;
  g.enableVertexAttribArray(aPos);
  g.vertexAttribPointer(aPos, 2, g.FLOAT, false, 0, 0);

  // 256² 噪声 LUT (LINEAR + REPEAT): 一次 fetch 替代 hash, 同时解决精度与速度
  const data = new Uint8Array(256 * 256 * 4);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 256) | 0;
  noiseTex = g.createTexture();
  g.activeTexture(g.TEXTURE0);
  g.bindTexture(g.TEXTURE_2D, noiseTex);
  g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, 256, 256, 0, g.RGBA, g.UNSIGNED_BYTE, data);
  g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MIN_FILTER, g.LINEAR);
  g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MAG_FILTER, g.LINEAR);
  g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_S, g.REPEAT);
  g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_T, g.REPEAT);

  loc = {};
  for (const n of [
    'uResolution',
    'uTime',
    'uMouse',
    'uMouseVel',
    'uStir',
    'uPress',
    'uStorm',
    'uIdle',
    'uMaskHalf',
    'uNoiseTex',
    'uPalA',
    'uPalB',
    'uPalC',
    'uPalD',
    'uPalE',
    'uBaseCol',
    'uTune1',
    'uTune2',
    'uGlow',
    'uRimA',
    'uRimB',
  ]) {
    loc[n] = g.getUniformLocation(prog, n);
  }
  loc.uTrail = g.getUniformLocation(prog, 'uTrail[0]');
  loc.uRipples = g.getUniformLocation(prog, 'uRipples[0]');
  g.useProgram(prog);
  g.uniform1i(loc.uNoiseTex, 0);
  return true;
}

function releaseGl() {
  if (gl) {
    if (program) gl.deleteProgram(program);
    if (vbo) gl.deleteBuffer(vbo);
    if (noiseTex) gl.deleteTexture(noiseTex);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
  gl = null;
  program = null;
  vbo = null;
  noiseTex = null;
}

function pickTier() {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const cores = navigator.hardwareConcurrency || 8;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const dpr = window.devicePixelRatio || 1;
  if (mem < 4) {
    resMul = 0.35;
    cap30 = true;
    trailN = 0;
    octaves = 3;
    useWarp2 = false;
  } else if (coarse || cores <= 4) {
    resMul = 0.5;
    trailN = 2;
    octaves = 3;
    useWarp2 = false;
  } else {
    resMul = 0.6 * Math.min(dpr, 1.5);
    trailN = 4;
    octaves = 4;
    useWarp2 = true;
  }
  trailView = trail.subarray(0, Math.max(1, trailN) * 4);
}

// program 就绪后的启动序列 (applySize 内的 draw 即首帧)
function activate() {
  applySize();

  // reduced-motion: 渲一帧精心定格的静态画面即停, 全部交互关闭
  reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  staticMode = reduceMq.matches;
  onReduceChange = () => {
    staticMode = reduceMq?.matches ?? false;
    if (staticMode) {
      stopLoop();
    } else if (gl && !destroyed) {
      lastDraw = performance.now();
      lastRaf = lastDraw;
      startLoop();
    }
  };
  reduceMq.addEventListener?.('change', onReduceChange);

  lastDraw = performance.now();
  lastRaf = lastDraw;
  lastInput = lastDraw;
  if (staticMode) {
    shaderTime = 47;
    idleF = 1;
    draw();
  } else {
    startLoop();
  }

  // 首帧就绪 → crossfade 接管, 完成后卸载 CSS 版
  glShown.value = true;
  fadeTimer = setTimeout(() => {
    cssGone.value = true;
    fadeTimer = null;
  }, 700);

  window.addEventListener('pointermove', onPointerMove, { passive: true });
  window.addEventListener('pointerdown', onPointerDown, { passive: true });
  window.addEventListener('pointerup', onPointerUp, { passive: true });
  window.addEventListener('pointercancel', onPointerCancel, { passive: true });
  window.addEventListener('contextmenu', onContextMenu); // 需 preventDefault, 不能 passive
  window.addEventListener('blur', onBlur, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
}

function initGl() {
  const c = canvasEl.value;
  if (!c || destroyed) return;
  // SwiftShader 等软渲染 failIfMajorPerformanceCaveat 直接拿不到 context → CSS 回退
  const attrs = {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    powerPreference: 'low-power',
    failIfMajorPerformanceCaveat: true,
  } as const;
  const g = (c.getContext('webgl', attrs) ||
    c.getContext('experimental-webgl', attrs)) as WebGLRenderingContext | null;
  if (!g) {
    toCss();
    return;
  }
  // 双保险: 显式排软渲染器 (headless CI 正是 SwiftShader, shader 一行不跑)
  const dbg = g.getExtension('WEBGL_debug_renderer_info');
  if (dbg) {
    const renderer = String(g.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? '');
    if (/swiftshader|llvmpipe|softpipe|software/i.test(renderer)) {
      toCss();
      return;
    }
  }
  // 无 highp fragment 的老设备 (Mali-400 一代) 直接回 CSS:
  // fp16 下 uTime 在 t≈30 就开始抽帧、流星 exp 域溢出, 与其全 shader 防御不如不跑
  const hp = g.getShaderPrecisionFormat(g.FRAGMENT_SHADER, g.HIGH_FLOAT);
  if (!hp || hp.precision === 0) {
    toCss();
    return;
  }
  gl = g;

  c.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    stopLoop();
    // watchdog: contextrestored 不保证到来 (GPU 进程被杀/低优先级 context 被驱逐),
    // 4s 没等到就回 CSS — 否则 cssGone=true 后两层背景全空, 永久裸底色
    if (lostTimer) clearTimeout(lostTimer);
    lostTimer = setTimeout(() => {
      lostTimer = null;
      toCss();
    }, 4000);
  });
  c.addEventListener('webglcontextrestored', () => {
    if (lostTimer) {
      clearTimeout(lostTimer);
      lostTimer = null;
    }
    restoreCount++;
    if (restoreCount > 1 || !gl) {
      toCss();
      return;
    }
    const prog = buildProgram(gl);
    if (!prog || !gl.getProgramParameter(prog, gl.LINK_STATUS) || !finishGlObjects(gl, prog)) {
      toCss();
      return;
    }
    program = prog;
    applySize(); // 含补绘 — staticMode 下也能恢复画面
    lastDraw = performance.now();
    lastRaf = lastDraw;
    if (!staticMode) startLoop();
  });

  pickTier();
  const prog = buildProgram(g);
  if (!prog) {
    toCss();
    return;
  }
  linkAsync(g, prog, (ok) => {
    if (destroyed) return;
    if (!ok || !finishGlObjects(g, prog)) {
      toCss();
      return;
    }
    program = prog;
    activate();
  });
}

// storm 下降沿: 从屏幕中心放出最后一圈大涟漪 — 潮水退去的仪式感收尾
watch(
  () => props.storm,
  (v) => {
    if (!v && prevStorm && gl) injectRipple(0, 0, 2.2);
    prevStorm = !!v;
  },
);

// 主题切换: 设新 target, 帧循环里 lerp 过渡; reduced-motion 停帧时直接 snap + 补绘一帧
watch(
  () => props.theme,
  (t) => {
    themeTarget = themeParams(THEMES[t ?? 'dusk']);
    if (staticMode && program) {
      themeCur.set(themeTarget);
      draw();
    }
  },
);

onMounted(() => {
  prevStorm = !!props.storm;
  glAlive.value = true;
  // Lighthouse 防线: window load 之后的空闲期才初始化, FCP/LCP/TBT 测量窗口零成本
  const idle = () => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    if (w.requestIdleCallback) w.requestIdleCallback(() => initGl(), { timeout: 2000 });
    else setTimeout(initGl, 600);
  };
  if (document.readyState === 'complete') {
    idle();
  } else {
    loadHandler = idle;
    window.addEventListener('load', loadHandler, { once: true });
  }
});

onBeforeUnmount(() => {
  destroyed = true;
  stopLoop();
  clearTimers();
  detachListeners();
  if (loadHandler) window.removeEventListener('load', loadHandler);
  releaseGl();
});
</script>

<template>
  <!-- crossfade 期间 canvas 与 CSS 版同时存在 (canvas 在上, opacity 过渡), fade 完卸载 CSS -->
  <AuroraBg v-if="!cssGone" :storm="storm" />
  <canvas
    v-if="glAlive"
    ref="canvasEl"
    aria-hidden="true"
    class="aurora-shader"
    :class="{ 'aurora-shader--on': glShown }"
  />
</template>

<style scoped>
.aurora-shader {
  position: fixed;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}
.aurora-shader--on {
  opacity: 1;
}
</style>
